import { WeatherIcon } from "@Components/common/icon/WeatherIcon";
import { Bars } from "@Components/forecast/Bars";
import { HourProps } from "@Components/forecast/hourly/ItemWrapper";
import { MaskedIcon } from "@Components/forecast/icon/MaskedIcon";
import { BarsLabel } from "@Components/forecast/label/BarsLabel";
import { HourLabel } from "@Components/forecast/label/HourLabel";
import { TemperatureLabel } from "@Components/forecast/label/TemperatureLabel";
import { useForecastAppContext } from "@Context/ForecastAppContext/ForecastAppContext";
import { useTranslations } from "@Context/TranslationContext/TranslationContext";
import SunIconUrl from "@Resources/images/iconsets/sun.svg";
import { formatRainProbability } from "@Utils/format";
import classNames from "classnames";

export const TemperatureItem = ({
  label,
  intervalType,
  hour,
  boldLabel,
  withDivider,
  isIconAnimated = false,
}: HourProps & {
  boldLabel: boolean;
  withDivider: boolean;
  isIconAnimated?: boolean;
}) => {
  const { symbolTexts } = useForecastAppContext();
  const { t } = useTranslations();
  const {
    symbol24_code: symbol_code,
    SUN_MIN: sunshineDuration,
    TTT_C: expectedTemperature,
    TTTFEEL_C: feltTemperature,
    RRR_MM: rainAmount,
    PROBPCP_PERCENT: rainProbability,
  } = hour;
  const isLessThanFivePercentProbability = rainProbability < 5;
  const barScalingFactor = intervalType === "1h" ? 9 : 3;

  function getSunMinPercent(sunshineDuration: number): number {
    const maxDuration = intervalType === "1h" ? 60 : 180;
    if (sunshineDuration <= 0) {
      return 0;
    }
    if (sunshineDuration >= maxDuration) {
      return 100;
    }
    return Math.round((100 / maxDuration) * sunshineDuration);
  }

  const topLabel = {
    value: rainAmount.toString(),
    className: classNames("m:after:content-['_mm']", {
      "m:text-warmgrey-700 m:dark:text-blue-400":
        isLessThanFivePercentProbability || rainAmount === 0,
    }),
    testId: "hourly-navigation-temperature-item-rain-amount",
  };
  const bottomLabel = {
    value: formatRainProbability(rainProbability),
    className: classNames("m:after:content-['_%']", {
      "m:text-warmgrey-700 m:dark:text-blue-400":
        isLessThanFivePercentProbability || rainAmount === 0,
    }),
    testId: "hourly-navigation-temperature-item-rain-probability",
  };

  return (
    <>
      <span
        className="m:sr-only m:h-[232px]! m:w-[64px]! m:lg:w-[80px]!"
        aria-label={t("navigation.a11yTemperatureLabel", {
          label: label,
          symbolTexts: symbolTexts[symbol_code],
          expectedTemperature: expectedTemperature,
          feltTemperature: feltTemperature,
          rainAmount: rainAmount,
          rainProbability: formatRainProbability(rainProbability),
        })}
        tabIndex={-1}
      />
      <div
        className="m:mt-2 m:flex m:flex-col m:items-center"
        aria-hidden="true"
        role="presentation">
        <HourLabel
          label={label}
          isBold={boldLabel}
          testId="hourly-navigation-temperature-item-hour"
        />
        <WeatherIcon
          symbolCode={symbol_code}
          altText={symbolTexts[symbol_code]}
          variant="forecast-hour"
          animated={isIconAnimated}
          testId="hourly-navigation-temperature-item-symbol"
        />
        <MaskedIcon
          iconUrl={SunIconUrl}
          fadePercentage={getSunMinPercent(sunshineDuration)}
          bgColor="m:bg-special-sunshine"
        />
        <TemperatureLabel
          temperature={expectedTemperature}
          feltTemperature={feltTemperature}
        />
      </div>
      <div className="m:flex m:flex-col" aria-hidden="true" role="presentation">
        <div className="m:relative">
          <Bars
            values={[rainAmount]}
            unit="mm"
            scalingFactor={barScalingFactor}
            withDivider={withDivider}
          />
        </div>
        <BarsLabel topLabel={topLabel} bottomLabel={bottomLabel} />
      </div>
    </>
  );
};
